import {
  BorrowingDetail,
  BorrowingDetailActions,
} from "@/components/borrowing/detail/BorrowingDetail";
import BorrowingResponsibilityConfirmation from "@/components/borrowing/BorrowingResponsibilityConfirmation";
import { Button } from "@/components/ui/button/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog/Dialog";
import LoadingIndicator from "@/components/ui/loading/LoadingIndicator";
import { useToast } from "@/lib/hooks/useToast";
import BorrowingService from "@/lib/services/borrowing/BorrowingService";
import { IDetailBorrowing } from "@/models/borrowing/DetailBorrowing";
import { UserFacingError } from "@/models/helper/UserFacingError";
import { useRoomBorrowingStore } from "@/store/room_borrowing/roomBorrowingStore";
import { useUserStore } from "@/store/user/userStore";
import { useMsal } from "@azure/msal-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const SubmitBorrowing = () => {
  const roomBorrowingStore = useRoomBorrowingStore();
  const userStore = useUserStore();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const convertTeam = () => {
    const team = roomBorrowingStore.data.team;
    const detail: IDetailBorrowing[] = [];
    team.forEach((t) => {
      detail.push({
        id: 0,
        borrowing_id: 0,
        student_nim: t.binusian_id,
        student_name: t.name,
      });
    });
    return detail;
  };
  const { toast } = useToast();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isResponsibilityConfirmed, setIsResponsibilityConfirmed] =
    useState(false);
  const { instance, accounts } = useMsal();

  const submitBorrowing = async () => {
    const borrowingData = roomBorrowingStore.data;
    const shiftIds: Array<number> = [];
    borrowingData.shift.forEach((shift) => {
      shiftIds.push(+shift.id);
    });
    setLoading(true);

    let date = borrowingData.date;
    if (typeof date === "string") {
      date = new Date(date);
    }

    try {
      let accessToken = undefined;

      if (borrowingData.need_chemicals && borrowingData.requested_materials?.length > 0) {
        const tokenResponse = await instance.acquireTokenSilent({
          account: accounts[0],
          scopes: ["user.read"],
        });
        accessToken = tokenResponse.accessToken;
      }

      const res = await BorrowingService.submitBorrowing(
        "student",
        date!,
        borrowingData.room,
        shiftIds,
        borrowingData.borrowing_reason_category,
        borrowingData.purpose,
        borrowingData.need_internet ?? false,
        userStore.data?.BinusianId ?? "",
        userStore.data?.Email ?? "",
        borrowingData.phone,
        borrowingData.is_private,
        borrowingData.need_chemicals,
        borrowingData.team,
        borrowingData.assets,
        borrowingData.pc_count,
        borrowingData.requested_materials,
        accessToken
      );

      roomBorrowingStore.reset();
      localStorage.removeItem("room_borrowing");
      navigate("/");
      toast({
        title: "Success.",
        description: res.message,
      });
    } catch (error) {
      if (error instanceof UserFacingError) {
        setErrorMessage(error.description);
        setShowErrorModal(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitConfirmation = () => {
    if (!isResponsibilityConfirmed) {
      toast({
        title: "",
        description: "Please confirm the borrowing responsibility first.",
      });
      return;
    }

    setShowConfirmation(true);
  };

  const handleConfirmSubmit = () => {
    submitBorrowing();
    setShowConfirmation(false);
  };

  // const handleCancelSubmit = () => {
  //   setShowConfirmation(false);
  // };

  return (
    <>
      <BorrowingDetail
        data={{
          title: "Borrowing Data Confirmation",
          assets: roomBorrowingStore.data.assets,
          borrowing_status: "Request",
          date: roomBorrowingStore.data.date
            ? roomBorrowingStore.data.date.toString()
            : "",
          finalize_note: "",
          reason: "",
          need_internet: roomBorrowingStore.data.need_internet ?? false,
          pc_count: roomBorrowingStore.data.pc_count ?? 0,
          student_nim_leader: userStore.data?.BinusianId,
          userData: {
            email: userStore.data?.Email,
            phone: roomBorrowingStore.data.phone,
            borrowing_reason_category:
              roomBorrowingStore.data.borrowing_reason_category,
            purpose: roomBorrowingStore.data.purpose,
            borrower_count: roomBorrowingStore.data.borrower_count,
          },
          created_at: "",
          id: 0,
          room: {
            has_assets:
              roomBorrowingStore.data.assets.length > 0 ? true : false,
            id: roomBorrowingStore.data.room,
            room_availability: true,
            room_code: roomBorrowingStore.data.room_code,
            room_name: "",
            room_privacy_category:
              roomBorrowingStore.data.room_privacy_category,
            capacity: roomBorrowingStore.data.capacity,
          },
          detail_borrowings: [...convertTeam()],
          detail_shifts: [...roomBorrowingStore.data.shift],
          semester: {
            id: 0,
            semester_description: "Current",
          },
          is_private_borrowing: roomBorrowingStore.data.is_private,
        }}
      >
        {roomBorrowingStore.data.need_chemicals && roomBorrowingStore.data.requested_materials?.length > 0 && (
          <div className="mt-6 flex flex-col gap-2 rounded-lg border bg-secondary/30 p-5 shadow-sm">
            <h4 className="text-sm font-semibold tracking-wide text-foreground uppercase">
              Requested Materials
            </h4>
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-2 text-sm text-muted-foreground">
              {roomBorrowingStore.data.requested_materials.map((mat, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between border border-border/50 rounded-md p-3 bg-background shadow-sm"
                >
                  <span className="font-medium text-foreground truncate mr-2" title={mat.name}>{mat.name}</span>
                  <span className="shrink-0 rounded-md bg-secondary px-3 py-1 font-semibold text-foreground border shadow-sm">
                    {mat.requestAmount} <span className="text-xs text-muted-foreground uppercase">{mat.unit}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <LoadingIndicator loading={loading} />
        <BorrowingResponsibilityConfirmation
          checked={isResponsibilityConfirmed}
          onCheckedChange={setIsResponsibilityConfirmed}
          className="mt-4"
        />
        <BorrowingDetailActions>
          <Button
            disabled={loading || !isResponsibilityConfirmed}
            variant={"blue"}
            onClick={handleSubmitConfirmation}
          >
            Submit
          </Button>
        </BorrowingDetailActions>
      </BorrowingDetail>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Submission</DialogTitle>
            <DialogDescription>
              Are you sure you want to submit this borrowing request?
              <br />
              <span style={{ color: "#facc15" }}>
                * You can only use the room if your borrowing request has been
                approved.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant={"ghost"}
              onClick={() => setShowConfirmation(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmSubmit}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showErrorModal} onOpenChange={setShowErrorModal}>
        <DialogContent className="sm:max-w-md w-full p-6">
          <DialogHeader className="pr-8">
            <DialogTitle className="text-xl font-semibold text-red-500">
              Submission Error
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 text-sm text-foreground bg-muted/50 p-3 rounded-lg border border-border" style={{ whiteSpace: 'pre-line' }}>
            {errorMessage}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SubmitBorrowing;
